import { forwardRef } from 'react';

import classNames from 'classnames';

import styles from './Card.module.css';

import { Box, type BoxProps } from '../Box';

export type CardProps = Omit<
  BoxProps,
  | 'backgroundColor'
  | 'borderColor'
  | 'borderRadius'
  | 'p'
  | 'px'
  | 'py'
  | 'pb'
  | 'pl'
  | 'pr'
  | 'pt'
> & {
  borderColor?: 'none' | 'chickpea' | 'cranberry' | 'kale' | 'oyster';
};

/**
 * Renders a generic container as a card.
 *
 * The main difference between a Card and a Box is that a Card has specific
 * responsive paddings, smaller on mobile and larger on desktop, a limited
 * selection of border colors, a border radius, and a set background color.
 * While a box can be customized extensively, a card is meant to conform to
 * specific TFD design system specifications.
 *
 * @cssVariable --tfd-card-border The default border of the card when the borderColor prop is undefined.
 * @cssVariable --tfd-card-border-radius The border radius of the card.
 * @cssVariable --tfd-card-padding-desktop The padding of the card on desktop screens.
 * @cssVariable --tfd-card-padding-mobile The padding of the card on mobile screens.
 * @see https://corgi-x.tfd.engineering/components/card
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ borderColor, className, ...props }, ref) => {
    return (
      <Box
        {...props}
        backgroundColor="White"
        className={classNames(
          styles.card,
          borderColor && styles[borderColor],
          className
        )}
        ref={ref}
      />
    );
  }
);
